import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useMerchant } from './MerchantContext'
import { getUIAttributes, setUIAttributes } from '../core/api-methods'
import { createUIAttributePayload, SFContextType } from '../utils/attributesParsers'
import { parseCheckoutUIData } from '../utils/checkoutUIParsers'
import { CheckoutUIConfigType } from '../types/merchantAttributes'

interface CheckoutUIContextType {
  getters: {
    checkoutUI: CheckoutUIConfigType | null
    isPriceStrikeThroughEnabled: boolean
    isOnePageCheckoutEnable: boolean
    isLoading: boolean
  }
  actions: {
    updateCheckoutUI: (config: Record<string, unknown>, opts?: { refreshEssentials?: boolean }) => Promise<any>
    mutateCheckoutUI: () => Promise<void>
    setCheckoutUILocal: (next: CheckoutUIConfigType) => void
  }
}

const CheckoutUIContext = createContext<CheckoutUIContextType | undefined>(undefined)

const CheckoutUIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    getters: { merchant },
    actions: { mutateMerchantAttributes }
  } = useMerchant()
  const [checkoutUI, setCheckoutUI] = useState<CheckoutUIConfigType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const fetchCheckoutUI = useCallback(async () => {
    try {
      setIsLoading(true)
      const response: any = await getUIAttributes(SFContextType.CHECKOUT)
      if (!response || response.error) {
        console.error('Failed to fetch checkout ui-attributes', response?.error)
        return
      }
      setCheckoutUI(parseCheckoutUIData(response?.data?.ui_attributes ?? {}))
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!merchant?.attributes) return
    fetchCheckoutUI()
  }, [merchant?.accountId])

  const updateCheckoutUI = useCallback(
    async (config: Record<string, unknown>, opts?: { refreshEssentials?: boolean }) => {
      const payload = createUIAttributePayload(SFContextType.CHECKOUT, config)
      const response: any = await setUIAttributes(payload)
      const uiAttributes = response?.data?.[0]?.ui_attributes
      if (uiAttributes) setCheckoutUI(parseCheckoutUIData(uiAttributes))
      if (opts?.refreshEssentials) mutateMerchantAttributes()
      return response
    },
    [mutateMerchantAttributes]
  )

  const mutateCheckoutUI = useCallback(async () => {
    await fetchCheckoutUI()
  }, [fetchCheckoutUI])

  const setCheckoutUILocal = useCallback((next: CheckoutUIConfigType) => {
    setCheckoutUI(next)
  }, [])

  const value: CheckoutUIContextType = {
    getters: {
      checkoutUI,
      isPriceStrikeThroughEnabled: Boolean(merchant?.attributes?.isPriceStrikeThroughEnabled),
      isOnePageCheckoutEnable: Boolean(merchant?.attributes?.isOnePageCheckoutEnable),
      isLoading
    },
    actions: { updateCheckoutUI, mutateCheckoutUI, setCheckoutUILocal }
  }

  return <CheckoutUIContext.Provider value={value}>{children}</CheckoutUIContext.Provider>
}

const useCheckoutUI = () => {
  const context = useContext(CheckoutUIContext)
  if (!context) throw new Error('useCheckoutUI must be used within a CheckoutUIProvider')
  return context
}

export { CheckoutUIProvider, useCheckoutUI }
